// In-place visual editing for email templates.
//
// Email templates are table layouts with inline styles and Mustache sections
// between table rows ({{#items}}<tr>…</tr>{{/items}}). Rich-text libraries
// re-serialize the whole document, which strips inline styles and lets the
// browser hoist those section tags out of the table. So instead we never
// round-trip the layout through the DOM: we find the "leaf" text blocks in the
// template *string* (a <p>, heading, cell… holding only text, inline tags and
// variables), make just those editable, and splice edits back into the
// original string at their offsets. Everything else stays byte-for-byte.

import Mustache from 'mustache';

export interface Block {
  /** Offsets of the block's inner content in the template string. */
  start: number;
  end: number;
}

const LEAF_TAGS = 'p|h[1-6]|div|td|th|li';
const OPEN_LEAF = new RegExp(`<(${LEAF_TAGS})\\b[^>]*>`, 'gi');
// Any block-level boundary; a leaf ends at the first one, which must be its own close tag.
const BOUNDARY = new RegExp(`<\\/?(?:${LEAF_TAGS}|table|thead|tbody|tfoot|tr|ul|ol|head|body|html)\\b[^>]*>`, 'i');
const SECTION_TAG = /\{\{\s*([#^/])/g;
const VAR_TAG = /\{\{\{\s*([\w.]+)\s*\}\}\}|\{\{\s*&?\s*([\w.]+)\s*\}\}/g;

/** Section nesting depth at each section-tag offset, for "is this inside {{#…}}?" checks. */
function sectionDepthAt(html: string, offset: number): number {
  let depth = 0;
  SECTION_TAG.lastIndex = 0;
  for (let m; (m = SECTION_TAG.exec(html)) && m.index < offset; ) {
    depth += m[1] === '/' ? -1 : 1;
  }
  return depth;
}

/**
 * Editable blocks, in document order. Skips blocks inside Mustache sections
 * (they repeat per item or toggle on flags — edit those in HTML source) and
 * blocks that contain section tags or no visible content.
 */
export function findBlocks(html: string): Block[] {
  const blocks: Block[] = [];
  const bodyStart = Math.max(0, html.search(/<body\b/i));
  OPEN_LEAF.lastIndex = bodyStart;
  for (let m; (m = OPEN_LEAF.exec(html)); ) {
    const tag = m[1].toLowerCase();
    const start = m.index + m[0].length;
    const rest = html.slice(start);
    const boundary = BOUNDARY.exec(rest);
    if (!boundary || boundary[0].toLowerCase() !== `</${tag}>`) continue;
    const end = start + boundary.index;
    const inner = html.slice(start, end);
    if (/\{\{\s*[#^/!>=]/.test(inner)) continue;
    if (!inner.replace(/<[^>]*>/g, '').trim()) continue;
    if (sectionDepthAt(html, start) > 0) continue;
    blocks.push({ start, end });
  }
  return blocks;
}

const marker = (i: number) => `\u0001SEG${i}\u0001`;

/** Turns `Dear {{recipientName}},` into text with non-editable variable chips. */
// Only text between tags gets chips — a variable inside an attribute
// (href="{{statusUrl}}") stays literal and round-trips with the attribute.
function withChips(inner: string): string {
  return inner
    .split(/(<[^>]*>)/)
    .map((part) =>
      part.startsWith('<')
        ? part
        : part.replace(VAR_TAG, (raw, triple, name) => {
            const label = triple || name;
            return `<span class="__var" contenteditable="false" data-raw="${escapeAttr(raw)}">${escapeHtml(label)}</span>`;
          })
    )
    .join('');
}

const DESIGN_CSS = `
  .__seg { outline: 1px dashed transparent; outline-offset: 3px; border-radius: 2px; cursor: text; }
  .__seg:hover { outline-color: #93c5fd; }
  .__seg:focus { outline: 2px solid #2563eb; }
  .__var { display: inline-block; font: 12px/1.6 ui-monospace, Menlo, monospace; color: #1e40af;
           background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 4px; padding: 0 4px; margin: 0 1px;
           vertical-align: baseline; font-style: normal; letter-spacing: 0; text-transform: none; }
`;

/**
 * The editing view: the template rendered with sample data, except each
 * editable block, which shows its source with variables as chips inside a
 * span tagged with its block index. The editor makes those spans editable
 * once its listeners are attached, so no keystroke can land unrecorded.
 */
export function buildDesignHtml(html: string, sample: Record<string, unknown>): string {
  const blocks = findBlocks(html);
  let marked = html;
  for (let i = blocks.length - 1; i >= 0; i--) {
    marked = marked.slice(0, blocks[i].start) + marker(i) + marked.slice(blocks[i].end);
  }
  let out: string;
  try {
    out = Mustache.render(marked, sample);
  } catch {
    out = marked;
  }
  blocks.forEach((b, i) => {
    const inner = html.slice(b.start, b.end);
    out = out.replace(
      marker(i),
      `<span class="__seg" data-seg="${i}" spellcheck="true">${withChips(inner)}</span>`
    );
  });
  // First thing in the document, so nothing in the template (scripts, onclick=…,
  // javascript: links) can run with the admin's same-origin access.
  const csp = `<meta http-equiv="Content-Security-Policy" content="script-src 'none'; object-src 'none'; base-uri 'none'">`;
  const style = `<style>${DESIGN_CSS}</style>`;
  const withStyle = /<\/head>/i.test(out) ? out.replace(/<\/head>/i, `${style}</head>`) : style + out;
  return /<head\b[^>]*>/i.test(withStyle)
    ? withStyle.replace(/<head\b[^>]*>/i, (m) => m + csp)
    : csp + withStyle;
}

const INLINE_KEEP = new Set(['A', 'STRONG', 'EM', 'U', 'SPAN', 'SMALL', 'SUP', 'SUB', 'S']);
const RENAME: Record<string, string> = { B: 'strong', I: 'em' };

/** Serializes an edited block back to template source: inline HTML + {{variables}}. */
export function serializeBlock(el: Element): string {
  // Browsers leave a placeholder <br> after a line break at the end of a block.
  return serializeChildren(el).replace(/(?:<br\/>)+$/, '');
}

function serializeChildren(el: Element): string {
  let out = '';
  el.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += escapeText(node.textContent || '');
      return;
    }
    // nodeType, not instanceof: the block lives in the editor iframe's own realm.
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    if (el.classList.contains('__var')) {
      out += el.getAttribute('data-raw') || '';
      return;
    }
    const tag = el.tagName;
    if (tag === 'BR') {
      out += '<br/>';
      return;
    }
    // Browsers wrap new lines in <div>/<p> inside contenteditable; keep them as line breaks.
    if (tag === 'DIV' || tag === 'P') {
      out += (out ? '<br/>' : '') + serializeChildren(el);
      return;
    }
    const name = RENAME[tag] || (INLINE_KEEP.has(tag) ? tag.toLowerCase() : null);
    if (!name) {
      out += serializeChildren(el);
      return;
    }
    const attrs = [...el.attributes]
      .filter((a) => !a.name.startsWith('data-') && a.name !== 'contenteditable')
      .map((a) => ` ${a.name}="${escapeAttr(a.value)}"`)
      .join('');
    out += `<${name}${attrs}>${serializeChildren(el)}</${name}>`;
  });
  return out;
}

/** Replaces block `index`'s inner content in the template string. */
export function applyBlockEdit(html: string, index: number, inner: string): string {
  const block = findBlocks(html)[index];
  if (!block) return html;
  return html.slice(0, block.start) + inner + html.slice(block.end);
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Typed text must never become template syntax: break up "{{" with a zero-width space.
function escapeText(s: string): string {
  return (
    escapeHtml(s.replace(/\u200b/g, ''))
      // Editors pad around chips with non-breaking spaces; keep a plain space and
      // only use &nbsp; for deliberate runs of spaces.
      .replace(/[ \u00a0]+/g, (run) => ' ' + '&nbsp;'.repeat(run.length - 1))
      .replace(/\{\{/g, '{\u200b{')
  );
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
